import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage, API } from "@/App";
import { useAuth } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { token, checkAuth } = useAuth();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus("error");
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus("error");
      toast.error(t("تعذر التحقق من الدفع", "Could not verify payment"));
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPaymentData(response.data);

      if (response.data.payment_status === "paid") {
        setStatus("success");
        toast.success(t("تم الدفع بنجاح!", "Payment successful!"));
        // Refresh user data to update subscription status
        await checkAuth();
      } else if (response.data.status === "expired") {
        setStatus("error");
        toast.error(t("انتهت صلاحية جلسة الدفع", "Payment session expired"));
      } else {
        // Continue polling
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error("Payment status error:", error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <div className="glass-card rounded-2xl p-8 max-w-md w-full text-center">
        {status === "loading" && (
          <>
            <Loader2 className="w-16 h-16 text-yellow-500 mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-white mb-2">
              {t("جاري التحقق من الدفع...", "Verifying payment...")}
            </h1>
            <p className="text-slate-400">
              {t("يرجى الانتظار", "Please wait")}
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {t("تم الدفع بنجاح!", "Payment Successful!")}
            </h1>
            <p className="text-slate-400 mb-6">
              {t("شكراً لاشتراكك في Arab Sanad", "Thank you for subscribing to Arab Sanad")}
            </p>
            {paymentData && (
              <div className="bg-slate-800 rounded-xl p-4 mb-6 text-right">
                <p className="text-slate-400 text-sm">{t("المبلغ", "Amount")}</p>
                <p className="text-white font-bold text-xl">${paymentData.amount} {paymentData.currency?.toUpperCase()}</p>
              </div>
            )}
            <Button onClick={() => navigate("/dashboard")} className="btn-primary w-full">
              {t("العودة للرئيسية", "Back to Dashboard")}
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {t("فشل الدفع", "Payment Failed")}
            </h1>
            <p className="text-slate-400 mb-6">
              {t("حدث خطأ أثناء معالجة الدفع", "An error occurred while processing payment")}
            </p>
            <div className="space-y-3">
              <Button onClick={() => navigate("/subscriptions")} className="btn-primary w-full">
                {t("حاول مرة أخرى", "Try Again")}
              </Button>
              <Button onClick={() => navigate("/dashboard")} variant="outline" className="btn-secondary w-full">
                {t("العودة للرئيسية", "Back to Dashboard")}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
